import clsx from "clsx";
import { FC, useCallback, useMemo, useRef, useState } from "react";

import { useCollapsibleIds, useProperty } from "@tsmono/react/hooks";
import { Icon, IconName } from "@tsmono/react/icons";

import type { TimelineSpan } from "../core";
import { type TimelineState } from "../hooks/useTimeline";
import type { UseTimelineConfigResult } from "../hooks/useTimelineConfig";
import type {
  MultiTimelineNav,
  TimelineMinimapData,
  TimelineViewStack,
} from "../hooks/useTranscriptTimeline";
import type { MarkerKind } from "../markers";
import {
  formatTokenCount,
  type PositionedMarker,
  type PositionedSpan,
  type RowLayout,
} from "../swimlaneLayout";
import { buildSelectionKey, parseSelection } from "../timelineEventNodes";

import { TimelineMinimap } from "./TimelineMinimap";
import { TimelineOptionsPopover } from "./TimelineOptionsPopover";
import { TimelineSelector } from "./TimelineSelector";
import styles from "./TimelineSwimLanes.module.css";

// =============================================================================
// Breadcrumb computation
// =============================================================================

export interface BreadcrumbSegment {
  /** Display name for this segment. */
  label: string;
  /** Selection key to navigate to this segment. */
  key: string;
}

/**
 * Builds breadcrumb segments from the layouts and selected key.
 *
 * The selected key encodes tree position (e.g. "transcript/build/test").
 * We find ancestor rows by matching prefix keys, producing a trail like:
 * [main, Build, Test] where "Test" is the currently selected row.
 */
export function buildBreadcrumbs(
  rows: ReadonlyArray<{ key: string; name: string; depth: number }>,
  selectedRowKey: string | null
): BreadcrumbSegment[] {
  if (!selectedRowKey) return [];

  // Build a lookup from key to row
  const byKey = new Map<string, { key: string; name: string; depth: number }>();
  for (const row of rows) {
    byKey.set(row.key, row);
  }

  // Walk the key segments to find ancestor rows.
  // Key format: "transcript/build/test" → ancestors are "transcript", "transcript/build"
  const parts = selectedRowKey.split("/");
  const segments: BreadcrumbSegment[] = [];

  for (let i = 1; i <= parts.length; i++) {
    const ancestorKey = parts.slice(0, i).join("/");
    const row = byKey.get(ancestorKey);
    if (row) {
      const label =
        row.depth === 0 && row.name === "solvers" ? "main" : row.name;
      // Adjacent same-named segments collapse to the deeper one (a wrapping
      // top-level agent often shares the root's name — "main › main" reads as
      // a bug); the deeper key keeps the more specific navigation target.
      const prev = segments[segments.length - 1];
      if (prev && prev.label === label) {
        segments[segments.length - 1] = { label, key: row.key };
      } else {
        segments.push({ label, key: row.key });
      }
    }
  }

  return segments;
}

// =============================================================================
// Types
// =============================================================================

/** Navigation subset of TimelineState needed by the swimlane component. */
export type TimelineNavigation = Pick<
  TimelineState,
  "node" | "selected" | "select" | "clearSelection"
>;

/** Header configuration. The root label and minimap span come from the
 *  swimlane's timeline state. */
export interface TimelineHeaderProps {
  /** Called on header click to scroll the view to the top. */
  onScrollToTop?: () => void;
  /** Minimap data (root time mapping + selection) for the zoom indicator. */
  minimap?: TimelineMinimapData;
  /** Scrubber position within the minimap (0–1), or null when idle. */
  scrubberProgress?: number | null;
  /** Called when the user scrubs the minimap. */
  onScrub?: (progress: number) => void;
  /** Timeline config for the options popover. When provided, shows the options button. */
  timelineConfig?: UseTimelineConfigResult;
  /** Number of utility agents elided from display. When > 0 (and utility
   *  agents are off), a small indicator renders next to the options button
   *  so hidden model calls never disappear without a trace. */
  hiddenUtilityCount?: number;
  /** Multi-timeline navigation. The selector renders when >1 timelines. */
  multiTimeline?: MultiTimelineNav;
  /** Punch-down view navigation. The back button renders when the stack is non-empty. */
  views?: TimelineViewStack;
}

export interface TimelineSwimLanesProps {
  /** Row layouts computed by computeRowLayouts. */
  layouts: RowLayout[];
  /** Timeline navigation state (selection). */
  timeline: TimelineNavigation;
  /** Header configuration. */
  header?: TimelineHeaderProps;
  /** Whether the swimlane is currently in sticky mode (opaque background). */
  isSticky?: boolean;
  /** Called when an error or compaction marker is clicked. */
  onMarkerNavigate?: (eventId: string, selectedKey?: string) => void;
  /** Headroom auto-collapse: true when scrolling down while sticky. */
  headroomCollapsed?: boolean;
  /** Called before a layout shift (toggle) so the headroom hook can reset
   *  its anchor and ignore the resulting scroll-position change. */
  onLayoutShift?: () => void;
  /** Map from row key → number of compaction regions (only for rows with compactions). */
  regionCounts?: ReadonlyMap<string, number>;
  /** Default collapsed state when user has no stored preference.
   *  Falls back to `isFlat` (layouts.length <= 1) when undefined. */
  defaultCollapsed?: boolean;
  /** Row key → highlight clip percentage (0–100).
   *  The selected branch clips at 100; ancestors clip at the fork marker position. */
  highlightedKeys?: ReadonlyMap<string, number>;
  /** Drill into a branch row as a standalone spliced timeline. */
  onPunchDown?: (rowKey: string, label: string) => void;
}

// =============================================================================
// TimelineSwimLanes
// =============================================================================

export const TimelineSwimLanes: FC<TimelineSwimLanesProps> = ({
  layouts,
  timeline,
  header,
  isSticky,
  onMarkerNavigate,
  headroomCollapsed = false,
  onLayoutShift,
  regionCounts,
  defaultCollapsed: defaultCollapsedProp,
  highlightedKeys,
  onPunchDown,
}) => {
  const { selected, select: onSelect, clearSelection } = timeline;

  // Collapse state — persisted across sessions.
  // Auto-collapse when the transcript is flat (single row, no children).
  // Omit defaultValue so we can distinguish "never set" (undefined) from
  // an explicit user choice.
  const [collapsed, setCollapsed] = useProperty<boolean>(
    "timeline",
    "swimlanesCollapsed"
  );
  const isFlat = layouts.length <= 1;

  // When the user explicitly toggles while headroom is collapsed, suppress
  // the headroom override until the scroll direction changes. Without this,
  // `headroomCollapsed` always wins via the `||` below and the toggle has
  // no visible effect.
  //
  // Uses the "adjust state during render" pattern to clear the override
  // when headroomCollapsed changes (avoiding effects + cascading renders).
  const [headroomOverride, setHeadroomOverride] = useState(false);
  const [prevHeadroomCollapsed, setPrevHeadroomCollapsed] =
    useState(headroomCollapsed);
  if (prevHeadroomCollapsed !== headroomCollapsed) {
    setPrevHeadroomCollapsed(headroomCollapsed);
    setHeadroomOverride(false);
  }

  // User preference OR headroom auto-collapse (overlay).
  // Headroom collapses on scroll-down; user preference takes over on scroll-up.
  const effectiveHeadroom = headroomCollapsed && !headroomOverride;
  const isCollapsed =
    (collapsed ?? defaultCollapsedProp ?? isFlat) || effectiveHeadroom;
  const toggleCollapsed = useCallback(() => {
    onLayoutShift?.();
    setHeadroomOverride(true);
    setCollapsed(!isCollapsed);
  }, [isCollapsed, setCollapsed, onLayoutShift]);

  // Row expand/collapse state — stored via shared property bag.
  // Keys that are collapsed have `true`; default is expanded.
  const [stableCollapsedBucket, setRowCollapsedById] = useCollapsibleIds(
    "timeline-swimlane-rows"
  );

  // Rows with visible child rows in the current layout (used for default
  // collapse logic — depth >= 1 parent rows start collapsed).
  const parentKeys = useMemo(() => {
    const keys = new Set<string>();
    for (const layout of layouts) {
      const prefix = layout.key + "/";
      for (const other of layouts) {
        if (other.key.startsWith(prefix)) {
          keys.add(layout.key);
          break;
        }
      }
    }
    return keys;
  }, [layouts]);

  // Rows that show a chevron toggle: rows with visible children OR rows
  // with branch markers (branches may not be visible when showBranches is
  // off, but the chevron signals they're expandable).
  const expandableKeys = useMemo(() => {
    const keys = new Set(parentKeys);
    for (const layout of layouts) {
      if (layout.markers.some((m) => m.kind === "branch")) {
        keys.add(layout.key);
      }
    }
    return keys;
  }, [layouts, parentKeys]);

  // Determine if a row is collapsed. Default: depth >= 1 parent rows start
  // collapsed (only first level shown). Explicit store entries override.
  // Rows that are expandable only via branch markers (not in parentKeys)
  // also default to collapsed since their branches aren't visible yet.
  const isRowCollapsed = useCallback(
    (rowKey: string): boolean => {
      const explicit = stableCollapsedBucket?.[rowKey];
      if (explicit !== undefined) return explicit;
      const layout = layouts.find((l) => l.key === rowKey);
      if (!layout) return false;
      // Branch rows default to expanded so the full fork tree is visible.
      if (layout.branch) return false;
      // Rows with only branch-marker children (not in parentKeys) default
      // to collapsed — the branches aren't visible until showBranches is on.
      if (!parentKeys.has(rowKey) && expandableKeys.has(rowKey)) return true;
      // Regular parent rows at depth >= 1 start collapsed.
      return layout.depth >= 1 && parentKeys.has(rowKey);
    },
    [stableCollapsedBucket, layouts, parentKeys, expandableKeys]
  );

  // Filter out rows whose ancestors are collapsed
  const visibleLayouts = useMemo(() => {
    return layouts.filter((layout) => {
      // Check if any ancestor is collapsed
      const parts = layout.key.split("/");
      for (let i = 1; i < parts.length; i++) {
        const ancestorKey = parts.slice(0, i).join("/");
        if (isRowCollapsed(ancestorKey)) {
          return false;
        }
      }
      return true;
    });
  }, [layouts, isRowCollapsed]);

  const handleToggleRowCollapse = useCallback(
    (rowKey: string) => {
      const current = isRowCollapsed(rowKey);
      // When expanding a row with branch markers while showBranches is off,
      // also enable showBranches so the branch child rows become visible.
      if (current && !header?.timelineConfig?.showBranches) {
        const layout = layouts.find((l) => l.key === rowKey);
        if (layout?.markers.some((m) => m.kind === "branch")) {
          header?.timelineConfig?.setShowBranches(true);
        }
      }
      setRowCollapsedById(rowKey, !current);
    },
    [isRowCollapsed, setRowCollapsedById, header?.timelineConfig, layouts]
  );

  // Branch marker click → ensure showBranches is on and expand the row.
  // This reveals nested branch rows beneath the clicked marker's row.
  //
  // Always set the row to expanded (not just when currently collapsed):
  // when showBranches is first enabled, the row doesn't yet have children
  // in the current render, so isRowCollapsed would return false (no parent
  // = no default collapse). Writing an explicit `false` to the store ensures
  // the row stays expanded once the next render adds branch child rows.
  const handleBranchMarkerClick = useCallback(
    (rowKey: string) => {
      if (!header?.timelineConfig?.showBranches) {
        header?.timelineConfig?.setShowBranches(true);
      }
      setRowCollapsedById(rowKey, false);
    },
    [header?.timelineConfig, setRowCollapsedById]
  );

  // Options popover / breadcrumb toggle → toggle showBranches on/off.
  // When turning branches off, clear selection if it points to a branch row
  // so the URL override in useTimeline doesn't force branches back on.
  const handleBranchToggle = useCallback(() => {
    const turningOff = header?.timelineConfig?.showBranches;
    header?.timelineConfig?.setShowBranches(!turningOff);
    if (turningOff && selected && /\/branch-/.test(selected)) {
      clearSelection();
    }
  }, [header?.timelineConfig, selected, clearSelection]);

  // Parse selection into row key + optional span index
  const parsedSelection = useMemo(() => parseSelection(selected), [selected]);
  const selectedRowKey = parsedSelection?.rowKey ?? null;

  // Compute breadcrumbs from the selected row's ancestry
  const breadcrumbs = useMemo(
    () => buildBreadcrumbs(layouts, selectedRowKey),
    [layouts, selectedRowKey]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      const rowKeys = visibleLayouts.map((l) => l.key);
      // Arrow keys navigate by row key (strip span index)
      const currentIndex = selectedRowKey
        ? rowKeys.indexOf(selectedRowKey)
        : -1;

      switch (e.key) {
        case "ArrowDown": {
          e.preventDefault();
          const next =
            currentIndex < rowKeys.length - 1 ? currentIndex + 1 : currentIndex;
          const key = rowKeys[next];
          if (key !== undefined) onSelect(key);
          break;
        }
        case "ArrowUp": {
          e.preventDefault();
          const prev = currentIndex > 0 ? currentIndex - 1 : 0;
          const key = rowKeys[prev];
          if (key !== undefined) onSelect(key);
          break;
        }
        case "Escape": {
          e.preventDefault();
          clearSelection();
          break;
        }
      }
    },
    [visibleLayouts, selectedRowKey, onSelect, clearSelection]
  );

  const parentRow = visibleLayouts[0];
  const childRows = visibleLayouts.slice(1);

  const branchConnectors = useMemo(
    () => computeBranchConnectors(visibleLayouts),
    [visibleLayouts]
  );

  const renderRow = (layout: RowLayout, displayName?: string) => {
    const isRowSelected = selectedRowKey === layout.key;
    const selectedSpanIndex = isRowSelected
      ? (parsedSelection?.spanIndex ?? null)
      : null;
    const selectedRegionIndex = isRowSelected
      ? (parsedSelection?.regionIndex ?? null)
      : null;
    const hasChildren = expandableKeys.has(layout.key);
    const isRowExpanded = hasChildren ? !isRowCollapsed(layout.key) : undefined;

    return (
      <SwimlaneRow
        key={layout.key}
        layout={layout}
        displayName={displayName}
        isRowSelected={isRowSelected}
        highlightClip={highlightedKeys?.get(layout.key)}
        selectedSpanIndex={selectedSpanIndex}
        selectedRegionIndex={selectedRegionIndex}
        regionCount={regionCounts?.get(layout.key)}
        isExpanded={isRowExpanded}
        onToggleExpand={
          hasChildren ? () => handleToggleRowCollapse(layout.key) : undefined
        }
        onSelectRow={() => onSelect(layout.key)}
        onSelectSpan={(spanIndex) =>
          onSelect(buildSelectionKey(layout.key, spanIndex))
        }
        onSelectRegion={(spanIndex, regionIndex) =>
          onSelect(buildSelectionKey(layout.key, spanIndex, regionIndex))
        }
        onBranchToggle={() => handleBranchMarkerClick(layout.key)}
        onMarkerNavigate={onMarkerNavigate}
        connector={branchConnectors.get(layout.key)}
        onPunchDown={
          layout.branch && onPunchDown
            ? () => onPunchDown(layout.key, layout.name)
            : undefined
        }
      />
    );
  };

  return (
    <div
      className={clsx(styles.swimlane, isSticky && styles.swimlaneSticky)}
      tabIndex={0}
      onKeyDown={handleKeyDown}
      role="grid"
      aria-label="Timeline swimlane"
    >
      {/* Pinned: header row (breadcrumbs + minimap) — always visible */}
      <div className={styles.pinnedSection}>
        {header && (
          <HeaderRow
            {...header}
            node={timeline.node}
            breadcrumbs={breadcrumbs}
            onBreadcrumbSelect={onSelect}
            onToggleBranches={handleBranchToggle}
          />
        )}
      </div>

      {/* Collapsible: parent row + child rows */}
      <div
        className={clsx(
          styles.collapsibleSection,
          isCollapsed && styles.collapsibleCollapsed
        )}
      >
        <div className={styles.collapsibleInner}>
          <div className={styles.scrollSection}>
            {parentRow &&
              renderRow(
                parentRow,
                parentRow.name === "solvers" ? "main" : undefined
              )}
            {childRows.map((layout) => renderRow(layout))}
          </div>
        </div>
      </div>

      {/* Collapse toggle on bottom border */}
      <button
        type="button"
        className={styles.collapseToggle}
        onClick={toggleCollapsed}
        title={isCollapsed ? "Expand swimlanes" : "Collapse swimlanes"}
      >
        <Icon name={isCollapsed ? "chevronDown" : "chevronUp"} />
      </button>
    </div>
  );
};

// =============================================================================
// Branch connectors
// =============================================================================

/** Describes the L-shaped connector from a parent's branch marker to a branch row. */
interface BranchConnector {
  /** Percentage position of the fork marker on the parent row. */
  markerLeft: number;
  /** Percentage position of the branch bar's left edge. */
  barLeft: number;
  /** Number of rows between the parent and this branch row. */
  rowGap: number;
}

const kBranchKeyPattern = /\/branch-([^/]*)-(\d+)$/;

/**
 * For each branch row, compute the connector from its parent's fork marker
 * down to the branch bar start.
 */
function computeBranchConnectors(
  layouts: RowLayout[]
): ReadonlyMap<string, BranchConnector> {
  const connectors = new Map<string, BranchConnector>();

  // Build index of layout positions by key for fast lookup.
  const indexByKey = new Map<string, number>();
  for (let i = 0; i < layouts.length; i++) {
    indexByKey.set(layouts[i]!.key, i);
  }

  for (let i = 0; i < layouts.length; i++) {
    const layout = layouts[i]!;
    if (!layout.branch) continue;

    // Extract branchedFrom UUID from the branch key.
    const match = kBranchKeyPattern.exec(layout.key);
    if (!match) continue;
    const branchedFrom = match[1]!;

    // Find parent: strip the /branch-... suffix to get the parent key.
    const parentKey = layout.key.replace(kBranchKeyPattern, "");
    const parentIndex = indexByKey.get(parentKey);
    if (parentIndex === undefined) continue;
    const parentLayout = layouts[parentIndex]!;

    // Find the branch marker on the parent with matching reference.
    const marker = parentLayout.markers.find(
      (m) => m.kind === "branch" && m.reference === branchedFrom
    );
    if (!marker) continue;

    // Branch bar start position.
    const barLeft = layout.spans[0]?.bar.left ?? 0;

    connectors.set(layout.key, {
      markerLeft: marker.left,
      barLeft,
      rowGap: i - parentIndex,
    });
  }

  return connectors;
}

// =============================================================================
// SwimlaneRow (internal)
// =============================================================================

interface SwimlaneRowProps {
  layout: RowLayout;
  /** Override the displayed label (defaults to layout.name). */
  displayName?: string;
  /** Whether this row is selected (whole row or a span within it). */
  isRowSelected: boolean;
  /** The span index that is sub-selected, or null if the whole row is selected. */
  selectedSpanIndex: number | null;
  /** The region index that is sub-selected, or null if no region is selected. */
  selectedRegionIndex: number | null;
  /** Number of compaction regions for this row (undefined = no compaction regions). */
  regionCount?: number;
  /** Whether the row's children are expanded. undefined = no children (leaf). */
  isExpanded?: boolean;
  /** Toggle expand/collapse for this row. Only provided for rows with children. */
  onToggleExpand?: () => void;
  /** Select the whole row (label click). */
  onSelectRow: () => void;
  /** Select a specific span within the row (bar click). */
  onSelectSpan: (spanIndex: number) => void;
  /** Select a specific region within a span. */
  onSelectRegion: (spanIndex: number | undefined, regionIndex: number) => void;
  onBranchToggle: () => void;
  onMarkerNavigate?: (eventId: string, selectedKey?: string) => void;
  /** Highlight clip percentage (0–100) for partial bar highlighting.
   *  undefined = no highlight. 100 = full bar. <100 = clip at fork marker. */
  highlightClip?: number;
  /** Connector line from parent's branch marker to this branch row. */
  connector?: BranchConnector;
  /** Open this branch as a standalone spliced view. Only set on branch rows. */
  onPunchDown?: () => void;
}

const SwimlaneRow: FC<SwimlaneRowProps> = ({
  layout,
  displayName,
  isRowSelected,
  highlightClip,
  selectedSpanIndex,
  selectedRegionIndex,
  regionCount,
  isExpanded,
  onToggleExpand,
  onSelectRow,
  onSelectSpan,
  onSelectRegion,
  onBranchToggle,
  onMarkerNavigate,
  connector,
  onPunchDown,
}) => {
  const hasMultipleSpans = layout.spans.length > 1;
  const hasChildren = isExpanded !== undefined;

  // Collect compaction marker positions for region segmentation.
  // Only applicable for single-span rows with compaction markers.
  const compactionMarkerPositions = useMemo(() => {
    if (!regionCount || regionCount <= 1) return null;
    const positions: number[] = [];
    for (const marker of layout.markers) {
      if (marker.compactionIndex !== undefined) {
        positions.push(marker.left);
      }
    }
    return positions.length > 0 ? positions : null;
  }, [regionCount, layout.markers]);

  const handleChevronClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onToggleExpand?.();
    },
    [onToggleExpand]
  );

  // Wrap onMarkerNavigate to include the row key for compaction markers,
  // so the bar gets selected atomically with the event navigation.
  const handleMarkerNavigate = useMemo(() => {
    if (!onMarkerNavigate) return undefined;
    return (eventId: string) => onMarkerNavigate(eventId, layout.key);
  }, [onMarkerNavigate, layout.key]);

  return (
    <div className={styles.row} role="row">
      {/* Label cell — depth-based indentation; clicking selects the whole row.
          Keyboard selection is the grid's own Arrow/Escape handling, and the
          cell takes tabIndex -1 so the grid keeps a single tab stop. */}
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events */}
      <div
        className={clsx(
          styles.label,
          isRowSelected && styles.labelSelected,
          highlightClip !== undefined &&
            !isRowSelected &&
            styles.labelHighlighted
        )}
        style={{ paddingLeft: `${0.3 + layout.depth * 0.5}rem` }}
        role="gridcell"
        tabIndex={-1}
        onClick={onSelectRow}
      >
        {hasChildren ? (
          <button
            type="button"
            className={styles.chevron}
            // tabIndex -1 like the cell above so the grid keeps a single
            // tab stop. That leaves the chevron pointer-only (as it was
            // before it became a button): ArrowLeft/ArrowRight can't serve
            // as the keyboard path here because useArrowStepper binds them
            // at document capture for prev/next sample stepping.
            tabIndex={-1}
            onClick={handleChevronClick}
            aria-expanded={isExpanded}
            aria-label={isExpanded ? "Collapse" : "Expand"}
          >
            <Icon name={isExpanded ? "chevronDown" : "chevronRight"} />
          </button>
        ) : (
          <span className={styles.chevronSpacer} />
        )}
        {displayName ??
          (layout.branch ? (
            <>
              <Icon name="solverDefault" /> {layout.name}
            </>
          ) : (
            layout.name
          ))}
        {onPunchDown && (
          <button
            type="button"
            className={styles.punchDownBtn}
            title="Open as standalone timeline"
            onClick={(e) => {
              e.stopPropagation();
              onPunchDown();
            }}
          >
            <Icon name="focusEnter" />
          </button>
        )}
      </div>

      {/* Bar area cell */}
      <div className={styles.barArea}>
        <div className={styles.barInner}>
          {/* Fills */}
          {layout.spans.map((span, spanIndex) => {
            // For multi-span rows: a bar is "selected" only if it's the sub-selected span,
            // or if the whole row is selected (no span index).
            // For single-span rows: selected when the row is selected.
            const isBarSelected =
              isRowSelected &&
              (!hasMultipleSpans ||
                selectedSpanIndex === null ||
                selectedSpanIndex === spanIndex);
            // "Dimmed" = row is selected but a different specific span is focused
            const isBarDimmed =
              isRowSelected &&
              hasMultipleSpans &&
              selectedSpanIndex !== null &&
              selectedSpanIndex !== spanIndex;

            // Use region-segmented rendering when this bar has compaction markers
            // and is the selected span (or only span).
            const useRegions =
              compactionMarkerPositions !== null &&
              (!hasMultipleSpans || selectedSpanIndex === spanIndex);

            if (useRegions) {
              return (
                <RegionBarFill
                  key={spanIndex}
                  span={span}
                  isParent={layout.isParent}
                  isBarSelected={isBarSelected}
                  isBarDimmed={isBarDimmed}
                  selectedRegionIndex={selectedRegionIndex}
                  compactionPositions={compactionMarkerPositions}
                  onSelectBar={() =>
                    hasMultipleSpans ? onSelectSpan(spanIndex) : onSelectRow()
                  }
                  onSelectRegion={(regionIndex) =>
                    onSelectRegion(
                      hasMultipleSpans ? spanIndex : undefined,
                      regionIndex
                    )
                  }
                  onDoubleClick={onToggleExpand}
                />
              );
            }

            return (
              <BarFill
                key={spanIndex}
                span={span}
                isParent={layout.isParent}
                isSelected={isBarSelected}
                isDimmed={isBarDimmed}
                onSelect={() =>
                  hasMultipleSpans ? onSelectSpan(spanIndex) : onSelectRow()
                }
                onDoubleClick={onToggleExpand}
                insetPx={connector ? 1.5 : undefined}
              />
            );
          })}

          {/* Highlight overlay — rendered as a sibling of .fill so it has
              independent opacity (not stacked with the fill's own opacity). */}
          {highlightClip !== undefined && !isRowSelected && (
            <HighlightOverlay spans={layout.spans} clipRight={highlightClip} />
          )}

          {/* Markers */}
          {layout.markers.map((marker, i) => (
            <MarkerGlyph
              key={i}
              marker={marker}
              onBranchToggle={onBranchToggle}
              onMarkerNavigate={handleMarkerNavigate}
            />
          ))}

          {/* Branch connector line */}
          {connector && <BranchConnectorLine connector={connector} />}
        </div>
      </div>

      {/* Token cell */}
      <div className={styles.tokens}>
        {formatTokenCount(layout.totalTokens)}
      </div>
    </div>
  );
};

// =============================================================================
// HeaderRow (internal)
// =============================================================================

interface HeaderRowProps extends TimelineHeaderProps {
  /** The root timeline span (labels the header and feeds the minimap). */
  node: TimelineSpan;
  breadcrumbs?: BreadcrumbSegment[];
  onBreadcrumbSelect?: (key: string) => void;
  /** Called when the branches toggle is clicked (handles selection cleanup). */
  onToggleBranches?: () => void;
}

const HeaderRow: FC<HeaderRowProps> = ({
  node,
  minimap,
  scrubberProgress,
  onScrub,
  onScrollToTop,
  breadcrumbs,
  onBreadcrumbSelect,
  timelineConfig,
  hiddenUtilityCount,
  multiTimeline,
  onToggleBranches,
  views,
}) => {
  const hasBreadcrumbs = breadcrumbs && breadcrumbs.length > 1;
  const rootDisplay = node.name === "solvers" ? "main" : node.name;

  const [optionsOpen, setOptionsOpen] = useState(false);
  const optionsButtonRef = useRef<HTMLButtonElement | null>(null);

  return (
    <div className={styles.breadcrumbRow}>
      {multiTimeline && multiTimeline.timelines.length > 1 && (
        <>
          <TimelineSelector
            timelines={multiTimeline.timelines}
            activeIndex={multiTimeline.activeIndex}
            onSelect={multiTimeline.setActive}
          />
          <span className={styles.breadcrumbDivider}>/</span>
        </>
      )}
      {views && views.stack.length > 0 && (
        <>
          <button
            type="button"
            className={styles.viewStackBack}
            onClick={views.pop}
            title="Back to branch overview"
          >
            <Icon name="chevronLeft" />
            {views.stack.at(-1)!.label}
          </button>
          <span className={styles.breadcrumbDivider}>/</span>
        </>
      )}
      {hasBreadcrumbs ? (
        <div className={styles.breadcrumbTrail}>
          {breadcrumbs.map((segment, i) => {
            const isLast = i === breadcrumbs.length - 1;
            return (
              <span key={segment.key} className={styles.breadcrumbSegment}>
                {i > 0 && <span className={styles.breadcrumbDivider}>/</span>}
                {isLast ? (
                  <span className={styles.breadcrumbCurrent}>
                    {segment.label}
                  </span>
                ) : (
                  <button
                    type="button"
                    className={styles.breadcrumbLink}
                    onClick={() => onBreadcrumbSelect?.(segment.key)}
                  >
                    {segment.label}
                  </button>
                )}
              </span>
            );
          })}
        </div>
      ) : (
        <button
          type="button"
          className={styles.breadcrumbCurrent}
          onClick={onScrollToTop}
        >
          {rootDisplay}
        </button>
      )}
      {timelineConfig && (
        <button
          ref={optionsButtonRef}
          type="button"
          className={styles.optionsButton}
          onClick={() => setOptionsOpen((prev) => !prev)}
          title="Timeline options"
          aria-label="Timeline options"
        >
          <Icon name="threeDots" />
        </button>
      )}
      {timelineConfig &&
        !timelineConfig.includeUtility &&
        (hiddenUtilityCount ?? 0) > 0 && (
          <button
            type="button"
            className={styles.hiddenUtility}
            onClick={() => timelineConfig.setIncludeUtility(true)}
            title="Show utility agents"
          >
            {hiddenUtilityCount} utility{" "}
            {hiddenUtilityCount === 1 ? "agent" : "agents"} hidden
          </button>
        )}
      {minimap && (
        <TimelineMinimap
          root={node}
          mapping={minimap.mapping}
          selection={minimap.selection}
          scrubberProgress={scrubberProgress}
          onScrub={onScrub}
        />
      )}
      {timelineConfig && onToggleBranches && (
        <TimelineOptionsPopover
          isOpen={optionsOpen}
          setIsOpen={setOptionsOpen}
          // eslint-disable-next-line react-hooks/refs -- positionEl accepts null; PopOver handles this in effects
          positionEl={optionsButtonRef.current}
          config={timelineConfig}
          onToggleBranches={onToggleBranches}
        />
      )}
    </div>
  );
};

// =============================================================================
// BarFill (internal)
// =============================================================================

interface BarFillProps {
  span: PositionedSpan;
  isParent: boolean;
  isSelected: boolean;
  /** Row is selected but a different span is focused. */
  isDimmed: boolean;
  onSelect: () => void;
  /** Toggle expand/collapse on double-click. Only for rows with children. */
  onDoubleClick?: () => void;
  /** Pixel inset from the left edge (for branch connector visibility). */
  insetPx?: number;
}

const BarFill: FC<BarFillProps> = ({
  span,
  isParent,
  isSelected,
  isDimmed,
  onSelect,
  onDoubleClick,
  insetPx,
}) => {
  const handleClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onSelect();
    },
    [onSelect]
  );

  const handleDoubleClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onDoubleClick?.();
    },
    [onDoubleClick]
  );

  return (
    <div
      className={clsx(
        styles.fill,
        isParent && styles.fillParent,
        isSelected && styles.fillSelected,
        isDimmed && styles.fillDimmed
      )}
      style={{
        left: insetPx
          ? `calc(${span.bar.left}% + ${insetPx}px)`
          : `${span.bar.left}%`,
        width: insetPx
          ? `calc(${span.bar.width}% - ${insetPx}px)`
          : `${span.bar.width}%`,
      }}
      title={span.description ?? undefined}
      role="presentation"
      onClick={handleClick}
      onDoubleClick={onDoubleClick ? handleDoubleClick : undefined}
    />
  );
};

// =============================================================================
// HighlightOverlay (internal) — partial bar highlight for branch context
// =============================================================================

/**
 * Renders an overlay on the bar area to indicate which portion of the row's
 * spans are "active" (shown in the transcript). Positioned as a sibling of
 * .fill so it has independent opacity instead of stacking with the bar's own.
 *
 * - clipRight = 100 → covers the full span range (e.g. the branch row itself).
 * - clipRight < 100 → covers from the first span's left edge to the clip
 *   percentage (e.g. a parent bar up to the fork point marker).
 */
const HighlightOverlay: FC<{
  spans: PositionedSpan[];
  /** Absolute bar-area percentage (0–100) where the highlight stops. */
  clipRight: number;
}> = ({ spans, clipRight }) => {
  const firstSpan = spans[0];
  if (!firstSpan) return null;

  const barLeft = firstSpan.bar.left;
  // For clipRight=100, extend to the rightmost span edge.
  const lastSpan = spans[spans.length - 1] ?? firstSpan;
  const barRight = lastSpan.bar.left + lastSpan.bar.width;
  const right = clipRight >= 100 ? barRight : Math.min(clipRight, barRight);
  const width = Math.max(0, right - barLeft);

  if (width <= 0) return null;

  return (
    <div
      className={styles.fillHighlight}
      style={{
        left: `${barLeft}%`,
        width: `${width}%`,
      }}
    />
  );
};

// =============================================================================
// RegionBarFill (internal) — segmented bar for compaction regions
// =============================================================================

interface RegionBarFillProps {
  span: PositionedSpan;
  isParent: boolean;
  /** Whether the whole bar is selected (row or span level). */
  isBarSelected: boolean;
  /** Whether the bar is dimmed (a sibling span is focused). */
  isBarDimmed: boolean;
  /** Currently selected region index, or null if no region is selected. */
  selectedRegionIndex: number | null;
  /** Left positions (%) of compaction markers within the bar area. */
  compactionPositions: number[];
  /** Select the whole bar (no region). */
  onSelectBar: () => void;
  /** Select a specific region within the bar. */
  onSelectRegion: (regionIndex: number) => void;
  onDoubleClick?: () => void;
}

/**
 * Renders a bar fill split into clickable region segments at compaction marker
 * positions. Each segment spans from one compaction marker to the next (or
 * bar start/end). When no region is selected, all segments look like one
 * continuous bar. When a region is selected, the selected segment is
 * highlighted and others are dimmed.
 */
const RegionBarFill: FC<RegionBarFillProps> = ({
  span,
  isParent,
  isBarSelected,
  isBarDimmed,
  selectedRegionIndex,
  compactionPositions,
  onSelectBar,
  onSelectRegion,
  onDoubleClick,
}) => {
  const barLeft = span.bar.left;
  const barRight = span.bar.left + span.bar.width;

  // Build region boundaries: [barLeft, marker1, marker2, ..., barRight]
  const boundaries = [barLeft, ...compactionPositions, barRight];
  const regionCount = boundaries.length - 1;

  // Track hover via React state so we can coordinate highlighting across segments.
  // When no region is selected, hovering any segment highlights all of them.
  // When a region is selected, only the hovered segment highlights.
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

  // Suppress hover highlighting immediately after a click so that the segment
  // under the cursor doesn't instantly highlight when selection state changes.
  // Cleared on the next mouseLeave, so fresh mouseEnter events work normally.
  const suppressHoverRef = useRef(false);

  const handleDoubleClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onDoubleClick?.();
    },
    [onDoubleClick]
  );

  // If the bar is dimmed (sibling span focused), render a single dimmed bar
  if (isBarDimmed) {
    return (
      <div
        className={clsx(
          styles.fill,
          isParent && styles.fillParent,
          styles.fillDimmed
        )}
        style={{
          left: `${barLeft}%`,
          width: `${span.bar.width}%`,
        }}
        title={span.description ?? undefined}
        role="presentation"
        onClick={(e) => {
          e.stopPropagation();
          onSelectBar();
        }}
      />
    );
  }

  const hasRegionSelection = isBarSelected && selectedRegionIndex !== null;

  return (
    <>
      {boundaries.map((start, i) => {
        if (i >= regionCount) return null;
        const end = boundaries[i + 1];
        if (end === undefined || end <= start) return null;

        const segmentLeft = start;
        const segmentWidth = end - start;

        const isSegmentSelected =
          hasRegionSelection && selectedRegionIndex === i;

        // Visual state for each segment depends on three levels:
        // 1. Bar not selected → base opacity; hover highlights all segments
        // 2. Bar selected, no region → selected opacity; hover dims non-hovered segments
        // 3. Region selected → selected segment bright, others dimmed; hover per-segment

        const isFirst = i === 0;
        const isLast = i === regionCount - 1;

        // Determine the opacity class. Exactly one should apply:
        let opacityClass: string | undefined;
        if (hasRegionSelection) {
          // State 3: a specific region is selected
          if (isSegmentSelected) {
            opacityClass = styles.fillSelected;
          } else if (hoveredIndex === i) {
            opacityClass = styles.regionHover;
          } else {
            opacityClass = styles.fillDimmed;
          }
        } else if (isBarSelected) {
          // State 2: bar selected, no region — hover previews region boundaries
          if (hoveredIndex !== null && hoveredIndex !== i) {
            // Non-hovered segments dim to show which region would be selected
            opacityClass = isParent ? styles.fillParent : styles.regionDefault;
          } else {
            opacityClass = styles.fillSelected;
          }
        } else {
          // State 1: bar not selected
          if (hoveredIndex !== null) {
            opacityClass = styles.regionHover;
          } else {
            opacityClass = isParent ? styles.fillParent : styles.regionDefault;
          }
        }

        return (
          <div
            key={i}
            className={clsx(
              styles.regionSegment,
              opacityClass,
              isFirst && styles.regionFirst,
              isLast && styles.regionLast,
              !isFirst && !isLast && styles.regionMiddle
            )}
            style={{
              left: `${segmentLeft}%`,
              width: `${segmentWidth}%`,
            }}
            title={span.description ?? undefined}
            role="presentation"
            onMouseEnter={() => {
              if (!suppressHoverRef.current) setHoveredIndex(i);
            }}
            onMouseLeave={() => {
              suppressHoverRef.current = false;
              setHoveredIndex(null);
            }}
            onClick={(e) => {
              e.stopPropagation();
              // Suppress hover so the segment under the cursor doesn't
              // instantly highlight after selection state changes.
              suppressHoverRef.current = true;
              setHoveredIndex(null);
              if (isBarSelected && selectedRegionIndex === null) {
                // Bar is already selected at whole level — drill into region
                onSelectRegion(i);
              } else if (hasRegionSelection && selectedRegionIndex === i) {
                // Clicking the already-selected region — back to whole bar
                onSelectBar();
              } else if (hasRegionSelection) {
                // A different region is selected — switch to this one
                onSelectRegion(i);
              } else {
                // Not selected at all — select the whole bar first
                onSelectBar();
              }
            }}
            onDoubleClick={onDoubleClick ? handleDoubleClick : undefined}
          />
        );
      })}
    </>
  );
};

// =============================================================================
// BranchConnectorLine (internal)
// =============================================================================

const kRowHeight = 18; // matches --swimlane-row-height

const BranchConnectorLine: FC<{ connector: BranchConnector }> = ({
  connector,
}) => {
  const { markerLeft, barLeft, rowGap } = connector;
  // y=0 is the top of the current (branch) row's barInner.
  // The parent row is rowGap rows above. Start the vertical line at the
  // center of the parent row — the branch marker diamond sits there.
  const topY = -(rowGap * kRowHeight) + kRowHeight / 2;
  const midY = kRowHeight / 2; // vertical center of this row

  // Always draw an L-shape: vertical down then horizontal right with arrow.
  // The horizontal goes from markerLeft to barLeft. When they're the same,
  // we still need a short stub so the ▸ arrow is visible.
  const endLeft = Math.max(barLeft, markerLeft + 0.5);

  return (
    <>
      <svg className={styles.branchConnector}>
        {/* Vertical segment: extends 0.5px past midY to overlap with the horizontal line at the corner */}
        <line
          x1={`${markerLeft}%`}
          y1={topY}
          x2={`${markerLeft}%`}
          y2={midY + 0.5}
          stroke="var(--vscode-descriptionForeground, #717171)"
          strokeWidth={1}
        />
        {/* Horizontal segment: from marker position rightward to branch bar */}
        <line
          x1={`${markerLeft}%`}
          y1={midY}
          x2={`${endLeft}%`}
          y2={midY}
          stroke="var(--vscode-descriptionForeground, #717171)"
          strokeWidth={1}
        />
      </svg>
      {/* Right-pointing arrowhead: positioned at the inset bar edge */}
      <div
        className={styles.connectorArrow}
        style={{ left: `calc(${endLeft}% + 4px)` }}
      />
    </>
  );
};

// =============================================================================
// MarkerGlyph (internal)
// =============================================================================

interface MarkerGlyphProps {
  marker: PositionedMarker;
  onBranchToggle: () => void;
  onMarkerNavigate?: (eventId: string, selectedKey?: string) => void;
}

const kMarkerIcons: Record<MarkerKind, IconName> = {
  error: "error",
  compaction: "compaction",
  branch: "fork",
};

const MarkerGlyph: FC<MarkerGlyphProps> = ({
  marker,
  onBranchToggle,
  onMarkerNavigate,
}) => {
  const kindClass =
    marker.kind === "error"
      ? styles.markerError
      : marker.kind === "compaction"
        ? styles.markerCompaction
        : styles.markerBranch;

  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLSpanElement>) => {
      e.stopPropagation();
      if (marker.kind === "branch") {
        onBranchToggle();
      } else if (marker.reference && onMarkerNavigate) {
        onMarkerNavigate(marker.reference);
      }
    },
    [marker.kind, marker.reference, onMarkerNavigate, onBranchToggle]
  );

  const className = clsx(styles.marker, kindClass);
  const glyph = marker.kind !== "error" && (
    <Icon name={kMarkerIcons[marker.kind]} />
  );

  if (marker.kind === "branch") {
    return (
      <button
        type="button"
        className={className}
        style={{ left: `${marker.left}%` }}
        title={marker.tooltip}
        onClick={handleClick}
        aria-label="Toggle branches"
      >
        {glyph}
      </button>
    );
  }

  // Error/compaction markers are mouse shortcuts onto events the transcript
  // already lists; the grid's arrow-key navigation is the keyboard path.
  return (
    <span
      className={className}
      style={{ left: `${marker.left}%` }}
      title={marker.tooltip}
      role="presentation"
      onClick={handleClick}
    >
      {glyph}
    </span>
  );
};
